import { useEffect, useMemo, useRef } from "react"

import apiClient from "@/services/api/apiClient"
import { SignaturePlacement } from "@/types/questionnaire/models"
import { useQueries } from "@tanstack/react-query"

/**
 * Resolves a questionnaire's placed signatures to object URLs for rendering
 * on the PDF canvas. Fetched per-placement from the questionnaire itself
 * (not the org signature library's thumbnail endpoint), because a placement
 * must keep rendering even after its signature is soft-deleted from the org
 * library — only removing the placement should make it disappear.
 *
 * One representative placement id is fetched per distinct signature id, so
 * the same signature placed twice on one PDF is only fetched once. Results
 * are keyed by signature id (not placement id) to match how callers look
 * up the url for a given placement.
 *
 * Object URLs are cached by blob identity in a ref (not React state) so
 * resolving them can't trigger a render loop; they're revoked once, when the
 * hook unmounts.
 */
export const useSignatureImageUrls = (
  questionnaireId: string,
  placements: Pick<SignaturePlacement, "id" | "signatureId">[],
): Record<string, string> => {
  const urlCacheRef = useRef<Map<string, { blob: Blob; url: string }>>(
    new Map(),
  )

  useEffect(() => {
    const cache = urlCacheRef.current
    return () => {
      cache.forEach(({ url }) => URL.revokeObjectURL(url))
      cache.clear()
    }
  }, [])

  const representatives = useMemo(() => {
    const bySignatureId = new Map<string, string>()
    for (const placement of placements) {
      if (!bySignatureId.has(placement.signatureId)) {
        bySignatureId.set(placement.signatureId, placement.id)
      }
    }
    return Array.from(bySignatureId, ([signatureId, placementId]) => ({
      signatureId,
      placementId,
    }))
  }, [placements])

  const { urlsById } = useQueries({
    queries: representatives.map(({ placementId }) => ({
      queryKey: [
        "questionnaires",
        questionnaireId,
        "signature-placements",
        placementId,
        "image",
      ] as const,
      queryFn: async () => {
        const response = await apiClient.get<Blob>(
          `/questionnaires/${questionnaireId}/signature-placements/${placementId}/image`,
          { responseType: "blob" },
        )
        return response.data
      },
      staleTime: Infinity,
      gcTime: Infinity,
    })),
    combine: (results) => {
      const urlsById: Record<string, string> = {}
      results.forEach((result, i) => {
        const { signatureId } = representatives[i]
        const blob = result.data
        if (!blob) return

        const cached = urlCacheRef.current.get(signatureId)
        if (cached && cached.blob === blob) {
          urlsById[signatureId] = cached.url
          return
        }
        if (cached) URL.revokeObjectURL(cached.url)
        const url = URL.createObjectURL(blob)
        urlCacheRef.current.set(signatureId, { blob, url })
        urlsById[signatureId] = url
      })
      return { urlsById }
    },
  })

  return urlsById
}
